import api from '@services/api';

class PermissionService {
    /**
     * Get all permissions for a specific user
     * @param {number} userId - User ID
     * @returns {Promise<Array>} List of user permissions
     */
    async getUserPermissions(userId) {
        try {
            const response = await api.permissions.getUserPermissions(userId);
            return response.data;
        } catch (error) {
            console.error(`Failed to get permissions for user ${userId}:`, error);
            throw error;
        }
    }

    /**
     * Grant a permission to a user
     * @param {number} userId - User ID
     * @param {string} permissionType - Permission type
     * @param {number|null} targetLocationId - Target location ID (optional)
     * @param {number|null} targetUserId - Target user ID (optional)
     * @param {string|null} notes - Optional notes
     * @returns {Promise<Object>} Created permission
     */
    async grantPermission(userId, permissionType, targetLocationId = null, targetUserId = null, notes = null) {
        try {
            const params = {
                user_id: userId,
                permission_type: permissionType
            };

            if (targetLocationId) {
                params.target_location_id = targetLocationId;
            }
            if (targetUserId) {
                params.target_user_id = targetUserId;
            }
            if (notes) {
                params.notes = notes;
            }

            const response = await api.permissions.grant(params);
            return response.data;
        } catch (error) {
            console.error('Failed to grant permission:', error);
            throw error;
        }
    }

    /**
     * Revoke a permission from a user
     * @param {number} userId - User ID
     * @param {string} permissionType - Permission type
     * @param {number|null} targetLocationId - Target location ID (optional)
     * @param {number|null} targetUserId - Target user ID (optional)
     * @returns {Promise<Object>} Success message
     */
    async revokePermission(userId, permissionType, targetLocationId = null, targetUserId = null) {
        try {
            const params = {
                user_id: userId,
                permission_type: permissionType
            };

            if (targetLocationId) {
                params.target_location_id = targetLocationId;
            }
            if (targetUserId) {
                params.target_user_id = targetUserId;
            }

            const response = await api.permissions.revoke(params);
            return response.data;
        } catch (error) {
            console.error('Failed to revoke permission:', error);
            throw error;
        }
    }

    /**
     * Update a specific permission
     * @param {number} permissionId - Permission ID
     * @param {Object} updateData - Data to update
     * @returns {Promise<Object>} Updated permission
     */
    async updatePermission(permissionId, updateData) {
        try {
            const response = await api.permissions.update(permissionId, updateData);
            return response.data;
        } catch (error) {
            console.error(`Failed to update permission ${permissionId}:`, error);
            throw error;
        }
    }

    /**
     * Delete a permission
     * @param {number} permissionId - Permission ID
     * @returns {Promise<void>}
     */
    async deletePermission(permissionId) {
        try {
            await api.permissions.delete(permissionId);
        } catch (error) {
            console.error(`Failed to delete permission ${permissionId}:`, error);
            throw error;
        }
    }

    /**
     * Get all locations for permission assignment
     * @returns {Promise<Array>} List of locations
     */
    async getLocations() {
        try {
            const response = await api.locations.getAll();
            return response.data.models || [];
        } catch (error) {
            console.error('Failed to get locations:', error);
            throw error;
        }
    }

    /**
     * Get all active users for permission assignment
     * @returns {Promise<Array>} List of active users
     */
    async getActiveUsers() {
        try {
            const response = await api.users.getActive();
            return response.data;
        } catch (error) {
            console.error('Failed to get active users:', error);
            throw error;
        }
    }

    /**
     * Check if current user has a specific permission
     * @param {string} permissionType - Permission type to check
     * @param {Object} context - Optional context (location_id, target_user_id)
     * @returns {Promise<boolean>} True if user has permission
     */
    async checkPermission(permissionType, context = {}) {
        try {
            const params = {};
            if (context.location_id) {
                params.location_id = context.location_id;
            }
            if (context.target_user_id) {
                params.target_user_id = context.target_user_id;
            }

            const response = await api.permissions.check(permissionType, params);
            return response.data.has_permission;
        } catch (error) {
            console.error(`Failed to check permission ${permissionType}:`, error);
            return false;
        }
    }
}

export const permissionService = new PermissionService();
export default permissionService;
